import React from "react";
import { User } from "lucide-react";

function PortraitPlaceholder({ initials, name, role, imageSrc, imageStyle = {}, className = "", style = {} }) {
  return (
    <figure
      data-testid={`founder-${name.split(" ")[0].toLowerCase()}`}
      className={`relative ${className}`}
      style={style}
    >
      {/* Portrait frame — uses imageSrc if provided, otherwise shows elegant placeholder */}
      <div
        className="relative w-full aspect-[3/4] overflow-hidden border border-[rgba(201,168,76,0.35)] bg-[var(--navy-2)] flex items-center justify-center"
        style={
          imageSrc
            ? {}
            : {
                backgroundImage:
                  "radial-gradient(120% 80% at 50% 0%, rgba(245,240,232,0.10) 0%, rgba(14,23,48,0) 60%), linear-gradient(180deg, #1a2545 0%, #0b1326 100%)",
              }
        }
      >
        {imageSrc ? (
          <img
            src={imageSrc}
            alt={`${name} — ${role}`}
            loading="lazy"
            className="w-full h-full object-cover"
            style={{
              filter: "grayscale(100%) contrast(1.05) brightness(0.95)",
              objectPosition: "center 18%",
              ...imageStyle,
            }}
          />
        ) : (
          <div className="text-center">
            <User size={42} className="mx-auto text-[var(--gold)]/60" strokeWidth={1.2} />
            <div className="mt-3 font-display text-[2.4rem] text-[var(--gold)]/80 tracking-wide">
              {initials}
            </div>
            <div className="mt-2 text-[0.65rem] tracking-[0.28em] uppercase text-[var(--ivory)]/40">
              Portrait
            </div>
          </div>
        )}
        {/* gold corner accents */}
        <span className="absolute top-3 left-3 w-5 h-5 border-l border-t border-[var(--gold)]/70" />
        <span className="absolute bottom-3 right-3 w-5 h-5 border-r border-b border-[var(--gold)]/70" />
      </div>
      <figcaption className="mt-4">
        <p
          className="font-display text-[1.2rem] leading-tight"
          style={{ color: "#F5F0E8" }}
        >
          {name}
        </p>
        <p className="text-[0.72rem] tracking-[0.22em] uppercase text-[var(--gold)] mt-1">
          {role}
        </p>
      </figcaption>
    </figure>
  );
}

export function About() {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="bg-[var(--navy)] text-[var(--ivory)] grain-overlay py-24 lg:py-32"
    >
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10 grid lg:grid-cols-[1.05fr_0.95fr] gap-14 lg:gap-24 items-center">
        {/* Left: copy */}
        <div className="reveal">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-10 bg-[var(--gold)]" />
            <span className="text-[0.7rem] tracking-[0.32em] uppercase text-[var(--gold)]">
              About Us
            </span>
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-[3.5rem] leading-[1.05]">
            The People Behind
            <br />
            <span className="italic text-[var(--gold)]">Ascend.</span>
          </h2>
          <p
            className="mt-6 font-display italic text-[1.35rem] sm:text-[1.5rem] leading-snug"
            style={{ color: "#F5F0E8" }}
          >
            Built from nothing. Now building yours.
          </p>

          <div className="mt-10 space-y-6 max-w-xl">
            <p
              className="text-[1rem] leading-[1.75]"
              style={{ color: "rgba(245,240,232,0.82)" }}
            >
              <span className="font-display text-[var(--gold)] text-[1.15rem]">
                Zain Mahal
              </span>{" "}
              started with no inheritance, no safety net — just determination.
              Over 25 years he built a multimillion pound UK property portfolio from
              the ground up. He's seen every market cycle, made every mistake so
              you don't have to, and knows exactly what a good deal looks like.
            </p>
            <p
              className="text-[1rem] leading-[1.75]"
              style={{ color: "rgba(245,240,232,0.82)" }}
            >
              <span className="font-display text-[var(--gold)] text-[1.15rem]">
                Imaan
              </span>{" "}
              brings five years' experience across estate agency, property sales
              and property management. Having worked with landlord portfolios and
              contributed to over £1 million in residential property transactions,
              she combines practical industry experience with specialist property
              sourcing training and a research-led approach to investment.
            </p>
            <div className="pt-2 flex items-center gap-4">
              <span className="h-px w-10 bg-[var(--gold)]" />
              <p
                className="font-display italic text-[1.05rem]"
                style={{ color: "#F5F0E8" }}
              >
                Together, they built Ascend to give you access to what took a
                lifetime to learn.
              </p>
            </div>
          </div>
        </div>

        {/* Right: stacked offset portraits */}
        <div className="reveal relative mx-auto w-full max-w-[520px]">
          <div className="relative grid grid-cols-2 gap-5 sm:gap-7">
            <PortraitPlaceholder
              initials="ZM"
              name="Zain Mahal"
              role="Founder"
              imageSrc="https://customer-assets-0z36b82j.emergentagent.net/job_premium-uk-property/artifacts/63fe1958ec527d39_859b807e-662c-4620-b2f3-d5d242d1516a%202.JPG"
              imageStyle={{
                filter: "grayscale(100%) contrast(1.05) brightness(0.95)",
                objectPosition: "center 25%",
              }}
              className="mt-0 sm:mt-10"
            />
            <PortraitPlaceholder
              initials="IM"
              name="Imaan"
              role="Co-Founder"
              imageSrc="https://customer-assets.emergentagent.com/job_premium-uk-property/artifacts/74a4mp93_IMG_7540.JPG"
              className="mt-10 sm:mt-0 sm:-translate-y-6"
            />
          </div>
          {/* faint decorative rule */}
          <span
            aria-hidden="true"
            className="hidden lg:block absolute -bottom-6 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[rgba(201,168,76,0.35)] to-transparent"
          />
        </div>
      </div>
    </section>
  );
}
